<template>
    <div class="enrollment">
        <div class="information">
            <div class="columns">
                <div class="column is-narrow is-hidden-touch">
                    <i class="fad fa-chalkboard-teacher has-text-primary fa-2x"></i>
                </div>
                <div class="column">
                    <div class="columns is-block-tablet is-flex-widescreen">
                        <div class="column">
                            <div class="has-text-weight-bold">
                                <i class="fad fa-chalkboard-teacher has-text-primary is-hidden-desktop mr-2"></i>
                                {{ enrollment.custom_name || enrollment.courses.title }}
                            </div>
                            <div v-if="enrollment.custom_name" class="enrollment-meta has-text-grey">
                                {{ enrollment.courses.title }}
                            </div>
                            <div v-if="enrollment.courses.organizations" class="enrollment-meta mb-3">
                                {{ enrollment.courses.organizations.title }}
                            </div>
                            <div v-if="!enrollment.completed_at && enrollment.expires_on && !isExpired" class="enrollment-meta mb-5">
                                <i class="far fa-calendar-clock mr-1"></i>This course enrollment will expire on <strong>{{ formatExpireDate(enrollment.expires_on) }}</strong>.
                            </div>
                            <div v-if="!enrollment.completed_at && !enrollment.reactivated_to_enrollment_id">
                                <!-- Hours Progress (if max_hours is set) -->
                                <div v-if="enrollment.max_hours > 0" class="mb-3">
                                    <b-progress
                                        type="is-info"
                                        size=""
                                        :value="Math.min((enrollment.total_logged_hours / enrollment.max_hours) * 100, 100)"
                                        class="enroll-progress mr-6"
                                    />
                                    <div class="enrollment-meta has-text-grey">
                                        {{ enrollment.total_logged_hours }} of {{ enrollment.max_hours }} hours logged
                                        ({{ Math.min(((enrollment.total_logged_hours / enrollment.max_hours) * 100), 100).toFixed(0) }}%)
                                    </div>
                                </div>
                                <!-- Lessons Progress -->
                                <b-progress type="is-primary" size="" :value="(enrollment.lessons_completed/enrollment.lessons)*100" class="enroll-progress mr-6"/>
                                <div class="enrollment-meta has-text-grey">{{ enrollment.lessons_completed }} of {{ enrollment.lessons }} lessons completed ({{ ((enrollment.lessons_completed / enrollment.lessons) * 100).toFixed(0) }}%).</div>
                            </div>
                            <!-- Retired via reactivation -- no progress bars, this record is done -->
                            <div v-else-if="enrollment.reactivated_to_enrollment_id">
                                <div class="enrollment-meta has-text-grey"><i class="far fa-rotate mr-2"></i>Reactivated into a new enrollment<template v-if="enrollment.reactivated_at"> on {{ enrollment.reactivated_at | moment('MMMM Do, YYYY') }}</template>.</div>
                            </div>
                            <div v-else>
                                <div class="enrollment-meta has-text-grey"><i class="far fa-check-circle mr-2 has-text-success"></i>Completed on {{ enrollment.completed_at | moment('MMMM Do, YYYY') }}.</div>
                            </div>
                        </div>
                        <div class="column has-text-right is-narrow">
                            <div v-if="!enrollment.completed_at">
                                <b-button v-if="enrollment.payment_locked" type="is-primary" size="" expanded disabled><i class="fas fa-lock mr-2"></i>Locked</b-button>
                                <router-link v-else-if="enrollment.reactivated_to_enrollment_id" :to="'/course/' + enrollment.reactivated_to_enrollment_id">
                                    <b-button type="is-primary" outlined size="" expanded><i class="fas fa-check mr-2"></i>Reactivated</b-button>
                                </router-link>
                                <router-link v-else-if="isExpired" :to="{name: 'reactivation-checkout'}">
                                    <b-button type="is-primary" size="" expanded><i class="fas fa-redo mr-2"></i>Reactivate</b-button>
                                </router-link>
                                <router-link v-else :to="'/course/' + enrollment.id">
                                    <b-button type="is-primary" size="" expanded><i class="fas fa-pencil mr-2"></i>Open Course</b-button>
                                </router-link>
                            </div>
                            <div v-else>
                                <template v-if="enrollment.courses.new_course">
                                    <router-link :to="'/course/' + enrollment.id">
                                        <b-button type="is-primary" outlined size="" expanded class="mb-2"><i class="fas fa-eye mr-2"></i>Review Course</b-button>
                                    </router-link>

                                    <router-link :to="{name: 'course-applications', params: {enrollmentId: enrollment.id}}">
                                        <b-button type="is-primary" size="" expanded><i class="fas fa-file-certificate mr-2"></i>Certificates</b-button>
                                    </router-link>
                                </template>

                                <!--                        <router-link v-else :to="'/legacy/' + enrollment.id">-->
                                <!--                            <b-button type="is-primary" outlined size="" expanded class="mb-2"><i class="fas fa-eye mr-2"></i>Review Course</b-button>-->
                                <!--                        </router-link>-->

                            </div>
                        </div>
                    </div>

                    <router-link :to="{name: 'reactivation-checkout'}" v-if="isExpired && !enrollment.completed_at && !enrollment.reactivated_to_enrollment_id">
                        <b-message type="is-warning">
                            <div class="columns is-gapless">
                                <div class="column is-narrow"><i class="fas fa-calendar-xmark fa-fw mr-3 has-text-warning"></i></div>
                                <div class="column">
                                    <div class="is-size-6 has-text-weight-bold">Course Enrollment Expired</div>
                                    <div class="is-size-7-mt-3">This course enrollment expired on <strong>{{ formatExpireDate(enrollment.expires_on) }}</strong>. Click <i class="fas fa-redo ml-1 mr-1"></i><strong>Reactivate</strong> to explore your options.</div>
                                </div>
                            </div>
                        </b-message>
                    </router-link>

                    <b-message type="is-warning" v-if="enrollment.payment_locked && !enrollment.completed_at">
                        <div class="columns is-gapless">
                            <div class="column is-narrow"><i class="fas fa-lock fa-fw mr-3 has-text-warning"></i></div>
                            <div class="column">
                                <div class="is-size-6 has-text-weight-bold">Locked &mdash; Payment Plan Pending</div>
                                <div class="is-size-7-mt-3">This course is part of a payment plan. It will unlock once the payment agreement is complete.</div>
                            </div>
                        </div>
                    </b-message>

                    <router-link :to="{name: 'course-applications', params: {enrollmentId: enrollment.id}}"  v-if="enrollment.completed_at && enrollment.isIncompleteApps">
                        <b-message type="is-warning">
                            <div class="columns is-gapless">
                                <div class="column is-narrow"><i class="fas fa-exclamation-triangle fa-fw mr-3 has-text-warning"></i></div>
                                <div class="column">
                                    <div class="is-size-6 has-text-weight-bold">Incomplete Applications or Forms</div>
                                    <div class="is-size-7-mt-3">You have incomplete applications or forms that you may wish to complete. Review <span style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</span>.</div>
                                </div>
                            </div>
                        </b-message>
                    </router-link>

                    <!-- Readiness-gated nags (completion redesign): shown only once the
                         student is genuinely done working — computed server-side -->
                    <router-link :to="{name: 'course-applications', params: {enrollmentId: enrollment.id}}" v-if="enrollment.application_readiness && enrollment.isMissingRequiredEndApps">
                        <b-message type="is-warning">
                            <div class="columns is-gapless">
                                <div class="column is-narrow"><i class="fas fa-exclamation-triangle fa-fw mr-3 has-text-warning"></i></div>
                                <div class="column">
                                    <!-- Generic wording on purpose: the required application may
                                         not be about hours/clock hours at all -->
                                    <div class="is-size-6 has-text-weight-bold">Action Required &mdash; Applications or Forms</div>
                                    <div class="is-size-7-mt-3">You've finished your coursework. This course requires an application or form to be completed in <span style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</span> to finish the course.</div>
                                </div>
                            </div>
                        </b-message>
                    </router-link>
                    <router-link :to="{name: 'course-applications', params: {enrollmentId: enrollment.id}}" v-else-if="enrollment.application_readiness && enrollment.isIncompleteApps">
                        <b-message type="is-info">
                            <div class="columns is-gapless">
                                <div class="column is-narrow"><i class="fas fa-circle-info fa-fw mr-3 has-text-info"></i></div>
                                <div class="column">
                                    <div class="is-size-6 has-text-weight-bold">Applications Available</div>
                                    <div class="is-size-7-mt-3">You've finished your coursework. There are applications or forms available in <span style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</span> that you may wish to complete.</div>
                                </div>
                            </div>
                        </b-message>
                    </router-link>
                </div>
            </div>


        </div>
        <div class="details">

        </div>

    </div>
</template>

<script>
    import moment from "moment";

    export default {
        name: "Enrollment",
        props: {
            enrollment: {
                required: true
            }
        },
        computed: {
            isExpired() {
                if (!this.enrollment.expires_on) return false;
                return moment.utc(this.enrollment.expires_on).isBefore(moment(), 'day');
            },
        },
        methods: {
            // expires_on is a DATE column — format in UTC so the day never
            // shifts backward in western timezones
            formatExpireDate(date) {
                return moment.utc(date).format('MMMM Do, YYYY');
            }
        }
    };
</script>

<style lang="scss" scoped>
    .enrollment {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);

        .information {
            padding: 1.5rem 1.5rem 1.5rem;
        }

        .enrollment-meta {
            font-size: 0.9rem;
        }
    }
</style>

<style lang="scss">
    .enroll-progress {
        margin-bottom: 0.2rem !important;

        .progress {
            height: 0.25rem !important;
        }
    }
</style>
